import React from "react";
import { useParams, Link } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import NotFound from "./NotFound";
import SEO from "../components/SEO";
import ServicesGrid from "../components/ServicesGrid";
import HowItWorks from "../components/HowItWorks";
import Brands from "../components/Brands";
import BeforeAfter from "../components/BeforeAfter";
import { useQuote } from "../context/QuoteContext";
import { Check, ArrowRight } from "lucide-react";
import { SITE_URL } from "../lib/site";
import { TextPhotoButton } from "../components/TextPhotoButton";
import { LocationQuoteCTA } from "../components/LocationQuoteCTA";
import { AudioQuickJobs } from "../components/audio/AudioQuickJobs";
import { HomeInstallGallery, TownInstalls } from "../components/InstallGallery";
import { TvMountingLocal, tvFaqs, tvTitle } from "../components/TvMountingLocal";
import { useContent } from "../lib/content";

const RM_AREA = { city: "Rocky Mount", county: "Nash and Edgecombe Counties", nearby: ["Nashville", "Tarboro", "Wilson"] };

const serviceContent = {
  "tv-installation": {
    title: "TV Installation & Wall Mounting",
    priceFrom: 179,
    subtitle: "Secure, professional TV mounting with hidden cables for a clean, modern finish.",
    h1: "TV Installation in Rocky Mount, NC",
    metaDescription: "Professional TV installation in Rocky Mount, NC. Mounting, hidden wires, and sound setup in one visit from a veteran-owned local installer since 2003. Call (252) 545-0011.",
    body: "Set Up Home Theater sells and installs TVs across Rocky Mount and Eastern North Carolina. One visit covers it: we deliver your TV, mount it, hide the wires, set up the sound, and teach you the remote. Mount It installs from $179 with the mount included, wires hidden where the wall allows, and devices connected. Bigger and higher-end TVs get the White Glove treatment with a two-person crew, calibration, programming, and packaging hauled away. Surround sound installs (5.1 and 7.1) are quoted from a video of your room. Veteran owned, installing TVs since 2003, with a 5-year install labor warranty. Wall types like brick, tile, concrete, stone, or plaster-and-lath get quoted after we see a video, so the price you approve is the price you pay. Text a photo or video of your room to (252) 545-0011 for a free remote quote, or book an on-site survey for $79, credited in full when you book.",
    faqHeading: "TV installation, your questions answered",
    faqs: [
      { q: "What does TV installation include?", a: "Mounting, wire concealment where the wall allows, device connection, and sound setup, all in one visit." },
      { q: "How much does TV installation cost?", a: "Mount It installs start at $179 with the mount included. Frame and G-series Gallery installs are $549 with the VersaBox in-wall box included. White Glove and surround sound are custom quoted." },
      { q: "Do you supply the TV and mount?", a: "Yes, as one bundle, or we install a TV and mount you already own." },
      { q: "What areas do you serve?", a: "Rocky Mount and Eastern North Carolina, including Nashville, Wilson, Tarboro, Greenville, and Spring Hope." },
    ],
    image: "https://images.pexels.com/photos/6020432/pexels-photo-6020432.jpeg",
    features: [
      "Wall mounting on any surface type",
      "Soundbar & shelf mounting",
      "Full cable concealment",
      "Table-top & cabinet setup",
    ],
  },
  "audio-installation": {
    title: "Audio Installation",
    priceFrom: 79,
    subtitle: "Whole-home audio and surround sound systems tuned to perfection.",
    metaDescription: "Expert audio installation in Rocky Mount, NC — surround sound, in-ceiling and multi-room speaker systems, professionally calibrated. Book a free quote today.",
    image: "https://images.unsplash.com/photo-1609702847389-b8aec1b0b929?crop=entropy&cs=srgb&fm=jpg&q=85",
    features: [
      "Surround sound systems",
      "In-ceiling & in-wall speakers",
      "Multi-room audio",
      "System calibration & tuning",
    ],
  },
  "home-cinema": {
    title: "Home Cinema Theater Installation",
    priceFrom: 299,
    subtitle: "Custom-designed home theaters that rival the big screen.",
    metaDescription: "Custom home cinema & home theater installation in Rocky Mount, NC. Projector, screen, acoustic room design and smart control. Build your dream theater — free quote.",
    image: "https://images.pexels.com/photos/5008383/pexels-photo-5008383.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    features: [
      "Projector & screen setup",
      "Acoustic room design",
      "Theater seating layout",
      "Smart control integration",
    ],
  },
  "religious-venue": {
    title: "Religious Venue Audio Installation",
    subtitle: "Crystal-clear sound for churches and worship venues of any size.",
    metaDescription: "Church & religious venue audio installation in Rocky Mount, NC. Clear distributed speakers, wireless microphones and live-stream sound for venues of any size.",
    image: "https://images.pexels.com/photos/37423127/pexels-photo-37423127.jpeg",
    features: [
      "Distributed speaker systems",
      "Wireless microphones",
      "Mixing consoles",
      "Live streaming audio",
    ],
  },
  "commercial-av": {
    title: "Commercial AV Installation",
    subtitle: "Professional audio-visual systems for offices, restaurants, retail, worship spaces and venues.",
    metaDescription: "Commercial AV installation in Rocky Mount & Eastern NC — conference room displays & video walls, digital signage, distributed audio, and control systems for offices, restaurants, bars, retail and venues. Get a business quote.",
    image: "https://images.unsplash.com/photo-1766802981801-4b4a9a1d8f1c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    features: [
      "Conference & board room displays",
      "Digital signage & video walls",
      "Restaurant, bar & retail audio/video",
      "Zoned audio & one-touch control",
    ],
  },
  "smart-home": {
    title: "Smart Home Device Setup",
    priceFrom: 399,
    subtitle: "Need a hand getting a smart device online? We set it up as an add-on to your AV project.",
    metaDescription: "Smart home device setup in Rocky Mount, NC — we install and configure smart lighting, thermostats, video doorbells and voice assistants as an add-on to your AV project. Get a free quote.",
    image: "https://images.pexels.com/photos/22307556/pexels-photo-22307556.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    features: [
      "Smart lighting & thermostats",
      "Voice assistant setup",
      "Video doorbell install",
      "Integrates with your AV system",
    ],
  },
  "security-cameras": {
    title: "Security Camera Setup (Add-On)",
    subtitle: "Adding a camera or video doorbell to your project? We mount and configure it as an add-on to your AV install.",
    metaDescription: "Security camera & video doorbell setup in Rocky Mount & Nash County, NC — offered as an add-on to your AV installation. Indoor/outdoor cameras mounted and configured. Get a free quote.",
    image: "https://images.unsplash.com/photo-1567690187548-f07b1d7bf5a9",
    features: [
      "Indoor & outdoor cameras",
      "Video doorbell setup",
      "Mobile app configuration",
      "Add-on to any AV project",
    ],
  },
  "motorized-shading": {
    title: "Lutron Automated Motorized Shading",
    subtitle: "Custom-measured, quiet motorized shades and wireless window treatments — designed and installed by consultation.",
    metaDescription: "Custom Lutron motorized shading & automated window treatments in Rocky Mount, Wilson & Nashville, NC. Roller, honeycomb & wireless battery-powered shades — professional measurement, design and installation. Schedule your free consultation.",
    image: "https://images.unsplash.com/photo-1761330439180-2bba3bbb8eb2?crop=entropy&cs=srgb&fm=jpg&q=85",
    features: [
      "Custom in-home measurement",
      "Motorized roller & honeycomb shades",
      "Wireless, battery-powered options",
      "Whisper-quiet automated control",
      "Scene, schedule & voice integration",
      "Professional design & installation",
    ],
    faqs: [
      {
        q: "How long do the batteries last in wire-free motorized shades?",
        a: "Lutron battery-powered shades typically run 3–5 years on a single set of batteries under normal daily use. Recharging or swapping takes just minutes, and the app or remote warns you well before power runs low — so you're never left guessing.",
      },
      {
        q: "Do motorized shades require wiring or an electrician?",
        a: "Not necessarily. Lutron offers fully wire-free, battery-powered shades that install with no electrical work, making them ideal for existing homes and rooms without nearby power. For new construction or larger installs we can also hard-wire for a permanent, maintenance-free solution — we'll recommend the best option during your free measurement.",
      },
      {
        q: "Are the shades controlled by remote, app or voice?",
        a: "All three. Control your shades with a handheld Lutron remote, the smartphone app from anywhere, or your voice through Alexa, Google Assistant and Apple HomeKit. You can also set schedules and scenes so shades raise and lower automatically throughout the day.",
      },
      {
        q: "What warranty comes with Lutron motorized shading?",
        a: "Lutron backs its motorized shading systems with a strong manufacturer warranty, and every installation we perform is fully guaranteed by our licensed, insured team. We handle any warranty service locally so you're covered start to finish.",
      },
      {
        q: "Do you offer free in-home measurement and consultation?",
        a: "Yes. We provide free custom measurement and design consultation across Rocky Mount, Wilson, Nashville and Eastern North Carolina. We'll assess your windows, discuss fabrics and automation options, and give you a no-obligation quote.",
      },
    ],
  },
  "areas-we-serve": {
    title: "Areas We Serve",
    subtitle: "Serving Rocky Mount, Nash County and communities across Eastern North Carolina.",
    metaDescription: "Set Up Home Theater serves Rocky Mount, Nash County, Edgecombe County and Eastern North Carolina with TV, audio, home cinema and smart home installation.",
    image: "https://images.unsplash.com/photo-1567690187548-f07b1d7bf5a9",
    features: [
      "Rocky Mount, NC",
      "Nash County, NC",
      "Edgecombe County, NC",
      "Surrounding areas",
    ],
  },
  "rocky-mount-nc": {
    title: "Rocky Mount & Nash County NC",
    subtitle: "Your local, trusted home technology installers right here in Rocky Mount.",
    h1: "TV Installation in Rocky Mount, NC",
    metaDescription: "Local TV, audio & smart home installation in Rocky Mount & Nash County, NC. Locally owned, with fast, same-day service. Get a free quote today.",
    body: "Set Up Home Theater is based in Rocky Mount and installs TVs across the city and surrounding Nash and Edgecombe County communities. Buy your TV from us or bring your own: we deliver it, mount it, hide the wires, set up the sound, and teach you the remote, all in one visit. Samsung Frame TV specialists, with the flush, picture-frame look and no wires showing. Mount It installs from $179, Frame/G-series Gallery install $549 with the VersaBox in-wall box included. Veteran owned, installing TVs since 2003, with a 5-year install labor warranty. Text a photo or video of your room to (252) 545-0011 for a fast quote.",
    image: "https://images.pexels.com/photos/7031762/pexels-photo-7031762.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    features: [
      "Same-day local service",
      "Locally owned & operated",
      "Fast response times",
      "Fully licensed & insured",
    ],
  },
};

const ServiceDetail = ({ slug: propSlug }) => {
  const params = useParams();
  const slug = propSlug || params.slug;
  const { openQuote } = useQuote();
  const content = useContent();

  if (slug === "all") {
    return (
      <main>
        <SEO
          title="Our Services"
          description="Explore our commercial & residential AV services — TV installation, audio installation, home cinema and commercial AV across North Carolina. Smart-home & security device setup available as an add-on."
          path="/services"
          jsonLd={{
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
              { "@type": "ListItem", position: 2, name: "Services", item: `${SITE_URL}/services` },
            ],
          }}
        />
        <PageHeader
          breadcrumb="Home / Services"
          title="Our Services"
          subtitle="From TV mounting to full home cinema builds, we handle every part of your home technology setup."
        />
        <ServicesGrid />
        <HowItWorks />
        <Brands />
      </main>
    );
  }

  if (!serviceContent[slug]) return <NotFound />;
  const data = serviceContent[slug];
  const canonicalPath = slug === "rocky-mount-nc" ? "/rocky-mount-nc" : `/services/${slug}`;
  const pageUrl = `${SITE_URL}${canonicalPath}`;

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: data.title,
    name: data.title,
    description: data.metaDescription || data.subtitle,
    url: pageUrl,
    image: data.image,
    ...(data.priceFrom
      ? { offers: { "@type": "Offer", priceSpecification: { "@type": "PriceSpecification", minPrice: data.priceFrom, priceCurrency: "USD" }, availability: "https://schema.org/InStock", url: pageUrl } }
      : {}),
    areaServed: {
      "@type": "AdministrativeArea",
      name: "Rocky Mount & Nash County, North Carolina",
    },
    provider: {
      "@type": "LocalBusiness",
      name: "Set Up Home Theater",
      telephone: "+1-252-545-0011",
      email: "chris@setuphometheater.com",
      address: {
        "@type": "PostalAddress",
        addressLocality: "Rocky Mount",
        addressRegion: "NC",
        addressCountry: "US",
      },
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Services", item: `${SITE_URL}/services` },
      { "@type": "ListItem", position: 3, name: data.title, item: pageUrl },
    ],
  };

  const isRM = slug === "rocky-mount-nc";
  const allFaqs = [...(isRM ? tvFaqs(content, RM_AREA) : []), ...(data.faqs || [])];
  const faqSchema = allFaqs.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: allFaqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }
    : null;

  return (
    <main>
      <SEO
        title={data.h1 || data.title}
        fullTitle={isRM ? tvTitle("Rocky Mount", content.prices.sizes[0]) : undefined}
        description={data.metaDescription || data.subtitle}
        path={canonicalPath}
        image={data.image}
        jsonLd={faqSchema ? [serviceSchema, breadcrumbSchema, faqSchema] : [serviceSchema, breadcrumbSchema]}
      />
      <PageHeader
        breadcrumb={`Home / Services / ${data.title}`}
        title={data.h1 || data.title}
        subtitle={data.subtitle}
      >
        {slug === "rocky-mount-nc" && <LocationQuoteCTA />}
      </PageHeader>
      <section className="section-pad">
        <div className="max-w-[1280px] mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-start">
          <div className="rounded-2xl overflow-hidden shadow-2xl order-1 lg:sticky lg:top-28">
            <img src={data.image} alt={data.title} className="w-full h-[420px] lg:h-[520px] object-cover" />
          </div>
          <div className="order-2">
            <span className="eyebrow">What's included</span>
            <h2 className="text-3xl font-extrabold text-[#0a1f44] mt-4 mb-5">
              Professional, reliable, done right
            </h2>
            {data.body ? <p data-testid="service-body" className="text-slate-500 leading-relaxed mb-6">{data.body}</p> : <p className="text-slate-500 leading-relaxed mb-6">
              Our expert technicians deliver clean, professional installations
              tailored to your space and budget. Here's what you can expect with
              our {data.title.toLowerCase()} service.
            </p>}
            <div className="grid sm:grid-cols-2 gap-3 mb-8">
              {data.features.map((f) => (
                <div key={f} className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#eef4ff] flex items-center justify-center shrink-0">
                    <Check className="w-3.5 h-3.5 text-[#3b9dff]" />
                  </span>
                  <span className="text-[#0a1f44] font-medium text-sm">{f}</span>
                </div>
              ))}
            </div>
            {data.priceFrom && (
              <p className="mb-4 text-sm text-slate-500" data-testid="service-price-from">
                Starting at <span className="text-2xl font-extrabold text-[#0a1f44]">${data.priceFrom}</span>
                <span className="text-slate-400"> · final price confirmed with your free quote</span>
              </p>
            )}
            {slug === "audio-installation" && <AudioQuickJobs />}
            {slug === "tv-installation" && (
              <Link to="/tv-mounting-quote" data-testid="instant-quote-cta" className="inline-flex items-center gap-2 mb-4 px-5 py-3 rounded-full bg-[#0a1f44] text-white font-semibold text-sm hover:bg-[#12305f] transition-colors">⚡ Get your instant price in seconds <ArrowRight className="w-4 h-4" /></Link>
            )}
            {slug === "tv-installation" && <div className="mb-4"><TextPhotoButton testId="service-text-photo-btn" /></div>}
            {slug === "tv-installation" && <Link to="/tv-mounting-above-fireplace" data-testid="service-fireplace-link" className="block mb-4 text-sm font-semibold text-[#3b9dff] underline underline-offset-2">Mounting above a fireplace? See fireplace pricing</Link>}
            <button
              onClick={() => openQuote(slug === "commercial-av" ? "Commercial AV" : slug === "motorized-shading" ? "" : data.title, slug === "motorized-shading" ? "Lutron" : "")}
              className="flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#3b9dff] text-white font-semibold hover:bg-[#2b8ae8] transition-colors"
            >
              {slug === "motorized-shading" ? "Schedule Measurement & Quote" : "Get a Quote"} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>
      {isRM && <TvMountingLocal {...RM_AREA} />}
      {isRM && <TownInstalls town="rocky-mount" city="Rocky Mount" />}
      {slug === "audio-installation" && <HomeInstallGallery category="audio" testId="audio-recent-installs" heading="Real audio installs from our crew" />}
      {data.faqs && data.faqs.length > 0 && (
        <section className="section-pad bg-[#f5f8ff]" data-testid="service-faq">
          <div className="max-w-[860px] mx-auto px-5 lg:px-8">
            <span className="eyebrow">FAQs</span>
            <h2 className="text-3xl font-extrabold text-[#0a1f44] mt-4 mb-8">
              {data.faqHeading || "Motorized shading — your questions answered"}
            </h2>
            <div className="space-y-4">
              {data.faqs.map((f, i) => (
                <details
                  key={i}
                  className="group bg-white rounded-xl border border-slate-200 p-5 open:shadow-md transition-shadow"
                  data-testid={`faq-item-${i}`}
                >
                  <summary className="flex items-start justify-between gap-4 cursor-pointer list-none font-bold text-[#0a1f44]">
                    <span>{f.q}</span>
                    <span className="text-[#3b9dff] text-xl leading-none shrink-0 transition-transform group-open:rotate-45">+</span>
                  </summary>
                  <p className="text-slate-500 leading-relaxed text-sm mt-3">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}
      <HowItWorks />
      {slug === "tv-installation" && <BeforeAfter />}
      <Brands />
    </main>
  );
};

export default ServiceDetail;
